import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "@phosphor-icons/react";
import ScenepackUploader from "../ScenepackUploader";

/**
 * The scenepack uploader in a full-screen window, for use anywhere on the site
 * (the dashboard has its own). Opens straight into `card` (a movie that's already
 * on the site). `lockCharacter` makes everything dropped join that character.
 * Closing while something is uploaded but not published asks first.
 */
export default function UploadModal({ card, lockCharacter = "", title = "Upload scenepacks", onClose, onDone }) {
  const [pending, setPending] = useState(false);
  const pendingRef = useRef(false);
  pendingRef.current = pending;

  const close = () => {
    if (pendingRef.current && !window.confirm("You have an uploaded scenepack that hasn't been published yet. Closing now deletes it. Close anyway?")) return;
    onClose();
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return createPortal(
    // pointer-events: a Radix dialog that just closed can leave the page un-clickable for a moment
    <div className="fixed inset-0 bg-black/85 z-[120] flex items-start justify-center p-3 md:p-6 overflow-y-auto overscroll-contain" style={{ pointerEvents: "auto" }} data-testid="upload-modal">
      <div className="relative bg-[#1A1A1A] border border-white/15 rounded-sm w-full max-w-5xl p-4 sm:p-6 md:p-8 max-h-[94dvh] overflow-y-auto overscroll-contain my-auto">
        <button type="button" onClick={close} aria-label="Close" data-testid="upload-modal-close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/5 border border-white/10 hover:bg-[#880015] hover:border-[#880015] text-white/70 hover:text-white flex items-center justify-center transition z-10">
          <X size={16} weight="bold" />
        </button>
        <h2 className="font-display font-black text-xl sm:text-2xl mb-6 pr-12">{title}</h2>
        <ScenepackUploader presetCard={card} lockCharacter={lockCharacter} onPendingChange={setPending} onDone={() => { setPending(false); onDone?.(); }} />
      </div>
    </div>,
    document.body
  );
}
